/**
 * FlashcardGenerator.jsx  —  NISH FSLP
 * ─────────────────────────────────────────────────────────────────────────────
 * Layout:  Left sidebar = My Sets (always visible)
 *          Right panel  = Set Editor (pick images, title, assign children, save)
 *
 * Key rules:
 *  • Each set is isolated — images are scoped to that set only
 *  • New Set clears the workspace entirely
 *  • Only selected images are saved into the set
 *  • Sets can be assigned to children (stored as assignedChildren[] on Flashcard)
 *  • Assigned sets visible to parents (ParentDashboard — future)
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/auth/AuthContext";

const API = import.meta.env.VITE_API_URL || "http://localhost:5001/api";

// ─── Constants ─────────────────────────────────────────────────────────────
const CARD_TYPES = [
  { id: "image-only",  label: "Image Only",   icon: "🖼️"  },
  { id: "text-only",   label: "Text Only",    icon: "🔤"  },
  { id: "image-text",  label: "Image + Text", icon: "📸"  },
];
const LAYOUTS = [
  { id: 4, label: "2 × 2", cols: 2 },
  { id: 6, label: "2 × 3", cols: 2 },
  { id: 9, label: "3 × 3", cols: 3 },
];
const LANGUAGES = [
  { code: "en", name: "English",  flag: "🇬🇧" },
  { code: "ml", name: "മലയാളം", flag: "🇮🇳" },
  { code: "hi", name: "हिंदी",   flag: "🇮🇳" },
  { code: "ta", name: "தமிழ்",  flag: "🇮🇳" },
  { code: "te", name: "తెలుగు", flag: "🇮🇳" },
];

// ─── API helper ─────────────────────────────────────────────────────────────
async function apiFetch(method, path, body, headers = {}) {
  const opts = {
    method,
    headers: { "Content-Type": "application/json", ...headers },
  };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${API}${path}`, opts);
  const text = await res.text();
  let data;
  try { data = JSON.parse(text); } catch { data = {}; }
  if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
  return data;
}

// ─── Tiny helpers ───────────────────────────────────────────────────────────
function makeSvg(emoji, bg) {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">` +
    `<rect width="200" height="200" rx="8" fill="${bg}"/>` +
    `<text x="100" y="115" font-family="Arial" font-size="60" text-anchor="middle">${emoji}</text></svg>`
  )}`;
}

function relDate(iso) {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (d === 0) return "Today";
  if (d === 1) return "Yesterday";
  if (d < 7)   return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day:"numeric", month:"short" });
}

// ─── Sub-components ─────────────────────────────────────────────────────────
function Spinner({ size = 20 }) {
  return (
    <div style={{ width:size, height:size, border:`3px solid #dce3f5`,
                  borderTop:`3px solid #003366`, borderRadius:"50%",
                  animation:"fg-spin .7s linear infinite", flexShrink:0 }} />
  );
}

function Toast({ msg, type = "success" }) {
  if (!msg) return null;
  const bg = type === "error" ? "#C0392B" : "#003366";
  return (
    <div style={{ position:"fixed", bottom:24, right:24, background:bg, color:"white",
                  padding:"11px 20px", borderRadius:10, fontSize:13, fontWeight:700,
                  zIndex:600, boxShadow:"0 8px 28px rgba(0,0,0,.25)", animation:"fg-slideUp .2s ease" }}>
      {msg}
    </div>
  );
}

// ─── IndicatorTagPicker ───────────────────────────────────────────────────────
// Shared chip-picker for the 40 NCF indicators, grouped by domain. Mirrors the
// one in CollectionManager.jsx — kept self-contained here so this file stays
// drop-in deployable. (Candidate for extraction to a shared component later.)
function IndicatorTagPicker({ taxonomy, selected, onToggle, compact }) {
  const [openDomain, setOpenDomain] = useState(null);
  if (!taxonomy) return (
    <div style={{ fontSize:11, color:"#aaa" }}>Loading indicator list…</div>
  );
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {taxonomy.domains.map(dom => {
        const inds = taxonomy.indicators.filter(i => i.domainId === dom.id);
        const selCount = inds.filter(i => selected.includes(i.id)).length;
        const open = openDomain === dom.id;
        return (
          <div key={dom.id} style={{ border:"1.5px solid #e0e7f0", borderRadius:9, overflow:"hidden" }}>
            <button onClick={() => setOpenDomain(open ? null : dom.id)}
              style={{ width:"100%", display:"flex", alignItems:"center", gap:8, padding: compact ? "6px 10px" : "8px 12px",
                       background: selCount > 0 ? "#EBF4FB" : "white", border:"none", cursor:"pointer", textAlign:"left" }}>
              <span style={{ fontSize: compact ? 13 : 15 }}>{dom.icon}</span>
              <span style={{ flex:1, fontSize: compact ? 11 : 12, fontWeight:700, color:"#003366" }}>{dom.label}</span>
              {selCount > 0 && (
                <span style={{ fontSize:10, fontWeight:800, color:"#003366", background:"white",
                               border:"1px solid #BDD7EE", borderRadius:10, padding:"1px 7px" }}>{selCount}</span>
              )}
              <span style={{ fontSize:11, color:"#888" }}>{open ? "▲" : "▼"}</span>
            </button>
            {open && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:5, padding:"10px 12px", background:"#FAFCFF" }}>
                {inds.map(ind => {
                  const on = selected.includes(ind.id);
                  return (
                    <button key={ind.id} onClick={() => onToggle(ind.id)}
                      title={ind.label}
                      style={{ padding:"4px 10px", borderRadius:8, fontSize:11, fontWeight:700,
                               border:`1.5px solid ${on ? "#003366" : "#e0e7f0"}`,
                               background: on ? "#003366" : "white",
                               color: on ? "white" : "#555", cursor:"pointer" }}>
                      {ind.id} {on ? "✓" : ""}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Collection Picker Modal ────────────────────────────────────────────────
function CollectionPickerModal({ onPick, onClose, getAuthHeader }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [sel,     setSel]     = useState(new Set());

  useEffect(() => {
    apiFetch("GET", "/collection?limit=500", null, getAuthHeader())
      .then(d => {
        if (d.success) setItems(d.images.map(doc => {
          const base = (import.meta.env.VITE_API_URL || "http://localhost:5001/api").replace("/api", "");

          const imageUrl = doc.imageUrl
            ? doc.imageUrl
            : doc.url
              ? `${base}${doc.url}`
              : null;

          // title is the reliable fallback when labels.en is empty
          const fallback = doc.title
            || doc.originalName?.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ")
            || "";

          // Merge: existing label takes priority per language; en falls back to title
          const labels = {
            en: doc.labels?.en?.trim() || fallback,
            ml: doc.labels?.ml?.trim() || "",
            hi: doc.labels?.hi?.trim() || "",
            ta: doc.labels?.ta?.trim() || "",
            te: doc.labels?.te?.trim() || "",
          };

          return {
            id: doc._id,
            imageUrl,
            labels,
            category: doc.category || "Custom",
            indicators: doc.indicators || [],
          };
        }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = items.filter(i =>
    !search || (i.labels?.en || "").toLowerCase().includes(search.toLowerCase())
  );

  const toggle = id => setSel(p => {
    const n = new Set(p);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.55)", zIndex:500,
                  display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
      onClick={onClose}>
      <div style={{ background:"#f5f7fa", borderRadius:16, width:760, maxWidth:"96vw",
                    maxHeight:"88vh", display:"flex", flexDirection:"column",
                    boxShadow:"0 24px 60px rgba(0,0,0,.28)" }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding:"16px 20px", borderBottom:"1px solid #e0e7f0", display:"flex",
                      alignItems:"center", gap:10, background:"white", borderRadius:"16px 16px 0 0" }}>
          <div style={{ fontWeight:800, fontSize:15, color:"#003366", flex:1 }}>🗂️ Browse Collection</div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
            style={{ border:"1.5px solid #dce3f5", borderRadius:8, padding:"6px 12px",
                     fontSize:12, outline:"none", width:180 }} />
          <span style={{ fontSize:12, color:"#888" }}>{sel.size} selected</span>
          <button onClick={onClose}
            style={{ background:"#f0f4ff", border:"none", borderRadius:8,
                     padding:"6px 12px", cursor:"pointer", color:"#666" }}>✕</button>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:16 }}>
          {loading ? (
            <div style={{ display:"flex", justifyContent:"center", padding:40 }}><Spinner /></div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign:"center", padding:60, color:"#aaa", fontSize:13 }}>
              No images in collection yet. Upload some first.
            </div>
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))", gap:8 }}>
              {filtered.map(item => {
                const isSel = sel.has(item.id);
                return (
                  <div key={item.id} onClick={() => toggle(item.id)}
                    style={{ borderRadius:10, overflow:"hidden", cursor:"pointer", position:"relative",
                             border:`2.5px solid ${isSel ? "#003366" : "#e0e7f0"}`,
                             boxShadow: isSel ? "0 0 0 3px rgba(0,51,102,.15)" : "none",
                             transition:"all .12s" }}>
                    <img src={item.imageUrl} alt={item.labels?.en || ""}
                      style={{ width:"100%", aspectRatio:"1", objectFit:"cover", display:"block" }} />
                    <div style={{ padding:"5px 6px", background:"white", fontSize:10, fontWeight:600,
                                  color:"#333", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {item.labels?.en || item.labels?.ml || item.labels?.hi || item.labels?.ta || item.labels?.te || "Untitled"}
                    </div>
                    {item.indicators?.length > 0 && (
                      <div style={{ position:"absolute", bottom:24, left:6, background:"rgba(0,51,102,.85)",
                                    color:"white", fontSize:9, fontWeight:800, padding:"1px 6px", borderRadius:6 }}
                        title={item.indicators.join(", ")}>
                        🎯 {item.indicators.length}
                      </div>
                    )}
                    {isSel && (
                      <div style={{ position:"absolute", top:6, right:6, width:20, height:20, borderRadius:"50%",
                                    background:"#003366", color:"white", display:"flex", alignItems:"center",
                                    justifyContent:"center", fontSize:11, fontWeight:900 }}>✓</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div style={{ padding:"12px 20px", borderTop:"1px solid #e0e7f0", display:"flex",
                      justifyContent:"flex-end", gap:8, background:"white", borderRadius:"0 0 16px 16px" }}>
          <button onClick={onClose}
            style={{ padding:"8px 16px", background:"white", color:"#666",
                     border:"1.5px solid #dce3f5", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer" }}>
            Cancel
          </button>
          <button disabled={sel.size === 0}
            onClick={() => onPick(filtered.filter(i => sel.has(i.id)))}
            style={{ padding:"8px 20px",
                     background: sel.size > 0 ? "#003366" : "#ccc",
                     color:"white", border:"none", borderRadius:8, fontWeight:700, fontSize:12,
                     cursor: sel.size > 0 ? "pointer" : "not-allowed" }}>
            Add {sel.size > 0 ? sel.size : ""} Image{sel.size !== 1 ? "s" : ""} to Set
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Card Tile ───────────────────────────────────────────────────────────────
function CardTile({ card, language, selected, onToggle, onRemove, onLabelChange }) {
  const label = card.labels?.[language] || card.labels?.en || "";
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(label);
  const inputRef = useRef();

  // Sync draft if language or card changes
  useEffect(() => {
    setDraft(card.labels?.[language] || card.labels?.en || "");
  }, [language, card.id]);

  const commitEdit = () => {
    setEditing(false);
    if (draft.trim() !== label) {
      onLabelChange(card.id, language, draft.trim());
    }
  };

  return (
    <div
      style={{
        borderRadius: 10, overflow: "hidden", cursor: "pointer", position: "relative",
        border: `2.5px solid ${selected ? "#003366" : "#e0e7f0"}`,
        boxShadow: selected ? "0 0 0 3px rgba(0,51,102,.12)" : "0 1px 4px rgba(0,0,0,.06)",
        background: "white", transition: "all .12s",
      }}
      onMouseEnter={e => !selected && (e.currentTarget.style.borderColor = "#a0b8d8")}
      onMouseLeave={e => !selected && (e.currentTarget.style.borderColor = "#e0e7f0")}
    >
      {/* Image — clicking selects/deselects */}
      <div onClick={() => !editing && onToggle(card.id)}>
        <img
          src={card.imageUrl || makeSvg("🃏", "#E8F0FE")}
          alt={label}
          style={{ width: "100%", aspectRatio: "1", objectFit: "cover", display: "block" }}
        />
      </div>

      {/* Indicator tag badge (read-only — edit set-level tags in the sidebar) */}
      {card.indicators?.length > 0 && (
        <div style={{ position:"absolute", top:6, left:6, background:"rgba(0,51,102,.85)",
                      color:"white", fontSize:9, fontWeight:800, padding:"1px 6px", borderRadius:6,
                      pointerEvents:"none" }}
          title={card.indicators.join(", ")}>
          🎯 {card.indicators.length}
        </div>
      )}

      {/* Label row — click pencil to edit */}
      <div
        style={{
          padding: "5px 7px", background: "white",
          borderTop: "1px solid #f0f4ff",
          display: "flex", alignItems: "center", gap: 4, minHeight: 28,
        }}
      >
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") { setEditing(false); setDraft(label); }
              e.stopPropagation();
            }}
            onClick={e => e.stopPropagation()}
            autoFocus
            style={{
              flex: 1, fontSize: 10, fontWeight: 600, color: "#003366",
              border: "1.5px solid #4A90D9", borderRadius: 5,
              padding: "2px 5px", outline: "none", minWidth: 0,
              background: "#f0f7ff",
            }}
          />
        ) : (
          <>
            <span
              style={{
                flex: 1, fontSize: 10, fontWeight: 600, color: "#333",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
            >
              {label || <span style={{ color: "#ccc", fontStyle: "italic" }}>No label</span>}
            </span>
            <span
              className="card-edit-btn"
              onClick={e => {
                e.stopPropagation();
                setDraft(card.labels?.[language] || card.labels?.en || "");
                setEditing(true);
                setTimeout(() => inputRef.current?.focus(), 50);
              }}
              style={{
                fontSize: 10, cursor: "pointer", color: "#888", flexShrink: 0,
                padding: "1px 4px", borderRadius: 4,
                opacity: 0, transition: "opacity .1s",
              }}
              title="Edit label"
            >
              ✎
            </span>
          </>
        )}
      </div>

      {/* Selected checkmark */}
      {selected && !editing && (
        <div style={{
          position: "absolute", top: 6, right: 6, width: 22, height: 22,
          borderRadius: "50%", background: "#003366", color: "white",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 900, pointerEvents: "none",
        }}>✓</div>
      )}

      {/* Remove button */}
      <button
        onClick={e => { e.stopPropagation(); onRemove(card.id); }}
        style={{
          position: "absolute", top: 6, left: 6, width: 20, height: 20,
          borderRadius: "50%", background: "rgba(192,57,43,.85)", color: "white",
          border: "none", cursor: "pointer", display: "flex", alignItems: "center",
          justifyContent: "center", fontSize: 11, opacity: 0, transition: "opacity .1s",
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = 1}
        className="card-remove-btn"
      >
        ×
      </button>
    </div>
  );
}

// ─── Sets Sidebar ────────────────────────────────────────────────────────────
function SetsSidebar({ sets, activeId, onSelect, onNew, onDelete, loading }) {
  const [search, setSearch] = useState("");
  const filtered = sets.filter(s => !search || s.title?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ width:260, flexShrink:0, borderRight:"1px solid #dce3f5", background:"white",
                  display:"flex", flexDirection:"column", height:"100%" }}>
      {/* Header */}
      <div style={{ padding:"14px 16px 10px", borderBottom:"1px solid #eef2fa" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
          <div style={{ fontWeight:800, fontSize:14, color:"#003366" }}>My Sets</div>
          <button onClick={onNew}
            style={{ background:"#003366", color:"white", border:"none", borderRadius:7,
                     padding:"6px 12px", fontSize:11, fontWeight:700, cursor:"pointer",
                     display:"flex", alignItems:"center", gap:5, whiteSpace:"nowrap" }}>
            + New Set
          </button>
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search sets…"
          style={{ width:"100%", border:"1.5px solid #dce3f5", borderRadius:8, padding:"6px 10px",
                   fontSize:11, outline:"none", boxSizing:"border-box", background:"#f8faff" }} />
      </div>

      {/* List */}
      <div style={{ flex:1, overflowY:"auto", padding:"8px 8px" }}>
        {loading ? (
          <div style={{ display:"flex", justifyContent:"center", padding:24 }}><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div style={{ padding:"32px 16px", textAlign:"center", color:"#b0becc" }}>
            <div style={{ fontSize:28, marginBottom:8 }}>🃏</div>
            <div style={{ fontSize:12, fontWeight:600, lineHeight:1.5 }}>
              {search ? "No matching sets" : "No sets yet.\nClick + New Set to begin."}
            </div>
          </div>
        ) : (
          filtered.map(s => {
            const isActive = s._id === activeId;
            const assigned = (s.assignedChildren || []).length;
            const indicatorCount = (s.indicators || []).length;
            return (
              <div key={s._id} onClick={() => onSelect(s)}
                style={{ borderRadius:10, padding:"10px 12px", marginBottom:4, cursor:"pointer",
                         background: isActive ? "#EBF4FB" : "transparent",
                         border: `1.5px solid ${isActive ? "#4A90D9" : "transparent"}`,
                         transition:"all .12s" }}
                onMouseEnter={e => !isActive && (e.currentTarget.style.background = "#f5f8ff")}
                onMouseLeave={e => !isActive && (e.currentTarget.style.background = "transparent")}>
                <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:12, color: isActive ? "#003366" : "#1a2e4a",
                                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                                  lineHeight:1.3, marginBottom:2 }}>
                      {s.title || "Untitled Set"}
                    </div>
                    <div style={{ fontSize:10, color:"#8fa5bc", lineHeight:1.4 }}>
                      {(s.cards || []).length} cards
                      {assigned > 0 && (
                        <span style={{ marginLeft:6, color:"#1B6B45", fontWeight:700 }}>
                          · {assigned} assigned
                        </span>
                      )}
                      {indicatorCount > 0 && (
                        <span style={{ marginLeft:6, color:"#003366", fontWeight:700 }}>
                          · 🎯 {indicatorCount}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize:9, color:"#b0becc", marginTop:2 }}>
                      {relDate(s.updatedAt || s.createdAt)}
                    </div>
                  </div>
                  {isActive && (
                    <button onClick={e => { e.stopPropagation(); onDelete(s._id); }}
                      style={{ background:"#fff0f0", border:"1px solid #f5c6c6", color:"#c0392b",
                               borderRadius:6, padding:"3px 7px", fontSize:10, cursor:"pointer",
                               flexShrink:0, fontWeight:700 }}>
                      🗑
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Assign Children Modal ───────────────────────────────────────────────────
function AssignModal({ children, current, onSave, onClose }) {
  const [sel, setSel] = useState(new Set(current));

  const toggle = id => setSel(p => {
    const n = new Set(p);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.52)", zIndex:500,
                  display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
      onClick={onClose}>
      <div style={{ background:"white", borderRadius:14, width:400, maxHeight:"80vh",
                    display:"flex", flexDirection:"column", boxShadow:"0 24px 60px rgba(0,0,0,.25)" }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding:"15px 20px", borderBottom:"1px solid #e0e7f0", display:"flex",
                      alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ fontWeight:800, fontSize:14, color:"#003366" }}>Assign to Children</div>
          <button onClick={onClose}
            style={{ background:"#f0f4ff", border:"none", borderRadius:7, padding:"5px 10px",
                     cursor:"pointer", color:"#666", fontSize:12 }}>✕</button>
        </div>
        <div style={{ overflowY:"auto", padding:16 }}>
          {children.length === 0 ? (
            <div style={{ textAlign:"center", padding:32, color:"#b0becc", fontSize:12 }}>
              No children assigned to you yet.
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {children.map(c => {
                const isSel = sel.has(c._id);
                return (
                  <div key={c._id} onClick={() => toggle(c._id)}
                    style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px",
                             borderRadius:9, border:`1.5px solid ${isSel ? "#003366" : "#e0e7f0"}`,
                             background: isSel ? "#EBF4FB" : "white", cursor:"pointer",
                             transition:"all .12s" }}>
                    <div style={{ width:32, height:32, borderRadius:"50%",
                                  background: c.color || "#1A4A6E", color:"white",
                                  display:"flex", alignItems:"center", justifyContent:"center",
                                  fontWeight:900, fontSize:11, flexShrink:0 }}>
                      {(c.name || "?").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase()}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:"#1a2e4a" }}>{c.name}</div>
                      {(c.disabilities || []).length > 0 && (
                        <div style={{ fontSize:10, color:"#8fa5bc" }}>
                          {c.disabilities.slice(0,2).join(", ")}
                        </div>
                      )}
                    </div>
                    <div style={{ width:20, height:20, borderRadius:"50%",
                                  border:`2px solid ${isSel ? "#003366" : "#c0cedc"}`,
                                  background: isSel ? "#003366" : "white",
                                  display:"flex", alignItems:"center", justifyContent:"center",
                                  flexShrink:0 }}>
                      {isSel && <span style={{ color:"white", fontSize:11, fontWeight:900 }}>✓</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div style={{ padding:"12px 20px", borderTop:"1px solid #e0e7f0", display:"flex",
                      justifyContent:"flex-end", gap:8 }}>
          <button onClick={onClose}
            style={{ padding:"8px 16px", background:"white", color:"#666",
                     border:"1.5px solid #dce3f5", borderRadius:8, fontWeight:700,
                     fontSize:12, cursor:"pointer" }}>Cancel</button>
          <button onClick={() => onSave([...sel])}
            style={{ padding:"8px 20px", background:"#003366", color:"white",
                     border:"none", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer" }}>
            Save ({sel.size} selected)
          </button>
        </div>
      </div>
    </div>
  );
}
// ─── Image to base64 helper ──────────────────────────────────────────────────
async function imgToBase64(src) {
  try {
    const res  = await fetch(src);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ─── PDF generator ───────────────────────────────────────────────────────────
async function generatePDF(cards, cardType, layout, language, title, settings) {
  const nishSrc  = new URL('../../assets/images/NISHlogo.png',  import.meta.url).href;
  const ncahtSrc = new URL('../../assets/images/NCAHTlogo.png', import.meta.url).href;
  const icmrSrc  = new URL('../../assets/images/ICMRlogo.png',  import.meta.url).href;

  const showLogos = settings?.showLogos !== false;
  const [nishB64, ncahtB64, icmrB64] = showLogos
    ? await Promise.all([imgToBase64(nishSrc), imgToBase64(ncahtSrc), imgToBase64(icmrSrc)])
    : [null, null, null];

  const PAGE_W = 794;
  const PAGE_H = 1123;

  const cols = layout.cols;   // 2 or 3
  const cpp  = layout.id;    // 4, 6, or 9
  const rows = cpp / cols;   // 2 or 3

  // Distribute page dimensions across cells with NO rounding error.
  // Strategy: give each column/row Math.floor px, then add the remainder
  // to the last column/row. This way sum is always exactly PAGE_W / PAGE_H.
  const baseColW  = Math.floor(PAGE_W / cols);
  const lastColW  = PAGE_W - baseColW * (cols - 1);
  const baseRowH  = Math.floor(PAGE_H / rows);
  const lastRowH  = PAGE_H - baseRowH * (rows - 1);

  // Cell sizes per position
  const cellW = (col) => col === cols - 1 ? lastColW : baseColW;
  const cellH = (row) => row === rows - 1 ? lastRowH : baseRowH;

  // Footer: fixed px, smaller for tighter layouts
  // 2×2: ~561px tall cells → 44px footer
  // 2×3: ~374px tall cells → 32px footer
  // 3×3: ~374px tall cells → 32px footer
  const FOOTER_H = rows === 2 ? 44 : 32;
  const CARD_PAD = 8;

  const LOGO_H = FOOTER_H - 8; // logo fits with 4px top+bottom breathing room

  // ── Logo helpers ──────────────────────────────────────────────────────────
  const nishImg = (h, op) =>
    nishB64
      ? '<img src="' + nishB64 + '" style="height:' + h + 'px;max-height:' + h + 'px;width:auto;object-fit:contain;opacity:' + (op||1) + ';display:block;" alt="NISH"/>'
      : '<span style="font-size:' + Math.round(h*0.38) + 'px;font-weight:900;color:#003366;font-family:Georgia,serif;">NISH</span>';

  const ncahtImg = (h) =>
    ncahtB64
      ? '<img src="' + ncahtB64 + '" style="height:' + h + 'px;max-height:' + h + 'px;width:auto;object-fit:contain;display:block;" alt="NCAHT"/>'
      : '<span style="font-size:9px;font-weight:900;color:#2563EB;font-family:Arial,sans-serif;">NCAHT</span>';

  const icmrImg = (h) =>
    icmrB64
      ? '<img src="' + icmrB64 + '" style="height:' + h + 'px;max-height:' + h + 'px;width:auto;object-fit:contain;display:block;" alt="ICMR"/>'
      : '<span style="font-size:9px;font-weight:900;color:#D97706;font-family:Arial,sans-serif;">ICMR</span>';

  // ── Footer — TABLE layout, fixed height ───────────────────────────────────
  // Tables are the most print-reliable fixed-height containers.
  // cellW is passed in because last column is wider.
  const footer = (isCover, cw) =>
    '<table style="' +
      'width:' + cw + 'px;' +
      'height:' + FOOTER_H + 'px;' +
      'min-height:' + FOOTER_H + 'px;' +
      'max-height:' + FOOTER_H + 'px;' +
      'table-layout:fixed;' +
      'border-collapse:collapse;' +
      'border-top:1px solid #D1D9E6;' +
      'background:#fff;' +
      'overflow:hidden;' +
    '" cellpadding="0" cellspacing="0">' +
    '<tr style="height:' + FOOTER_H + 'px;">' +
      // Left cell: NCAHT (cover) or NISH (card)
      '<td style="' +
        'width:' + Math.round(cw * 0.30) + 'px;' +
        'height:' + FOOTER_H + 'px;' +
        'padding:0 0 0 6px;' +
        'vertical-align:middle;' +
        'overflow:hidden;' +
      '">' +
        (isCover ? ncahtImg(LOGO_H) : nishImg(LOGO_H, 0.9)) +
      '</td>' +
      // Centre cell: URL
      '<td style="' +
        'height:' + FOOTER_H + 'px;' +
        'padding:0 3px;' +
        'vertical-align:middle;' +
        'text-align:center;' +
        'font-size:' + (FOOTER_H < 36 ? 6 : 7) + 'px;' +
        'color:#9CA3AF;' +
        'font-family:Arial,sans-serif;' +
        'font-weight:600;' +
        'white-space:nowrap;' +
        'overflow:hidden;' +
      '">https://iate.ncahtnish.in</td>' +
      // Right cell: ICMR (cover) or attribution text (card)
      '<td style="' +
        'width:' + Math.round(cw * 0.30) + 'px;' +
        'height:' + FOOTER_H + 'px;' +
        'padding:0 6px 0 0;' +
        'vertical-align:middle;' +
        'text-align:right;' +
        'overflow:hidden;' +
      '">' +
        (isCover
          ? icmrImg(LOGO_H)
          : '<span style="font-size:' + (FOOTER_H < 36 ? 5.5 : 6.5) + 'px;color:#9CA3AF;font-family:Arial,sans-serif;white-space:nowrap;">get yours from iate.ncahtnish.in</span>'
        ) +
      '</td>' +
    '</tr></table>';

  // ── Card body builders ────────────────────────────────────────────────────
  const coverBody = (cw, ch) => {
    const bodyH = ch - FOOTER_H;
    const logoH = Math.min(Math.round(bodyH * 0.30), 90);
    const fs    = cols === 3 ? 13 : 19;
    const fsSmall = cols === 3 ? 6.5 : 8.5;
    return (
      '<div style="' +
        'width:' + cw + 'px;' +
        'height:' + bodyH + 'px;' +
        'min-height:' + bodyH + 'px;' +
        'max-height:' + bodyH + 'px;' +
        'display:flex;flex-direction:column;' +
        'align-items:center;justify-content:center;' +
        'padding:10px 14px;box-sizing:border-box;' +
        'text-align:center;overflow:hidden;' +
      '">' +
        nishImg(logoH) +
        '<div style="font-family:Georgia,serif;font-size:' + fs + 'px;font-weight:900;color:#003366;margin:8px 0 3px;line-height:1.2;max-width:90%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' +
          (title || 'Flashcard Set') +
        '</div>' +
        '<div style="font-size:' + fsSmall + 'px;font-weight:700;color:#003366;letter-spacing:1.5px;text-transform:uppercase;">Open Curriculum</div>' +
        '<div style="font-size:' + fsSmall + 'px;font-weight:600;color:#6B7280;letter-spacing:1px;text-transform:uppercase;">Flashcards</div>' +
      '</div>'
    );
  };

  const cardBody = (card, cw, ch) => {
    const bodyH   = ch - FOOTER_H;
    const label   = card.labels?.[language] || card.labels?.en || '';
    const showImg = cardType !== 'text-only' && card.imageUrl;
    const showTxt = cardType !== 'image-only' && label;
    const labelH  = showTxt ? Math.max(18, Math.round(bodyH * 0.09)) : 0;
    const imgMaxH = bodyH - CARD_PAD * 2 - labelH - 2;
    const imgMaxW = cw - CARD_PAD * 2;
    const fsLabel = cols === 3 ? 10 : 13;

    // ── Text-only: full-cell centered bold text ───────────────────────────
    if (cardType === 'text-only') {
      const fsText = cols === 3
        ? Math.max(16, Math.round(bodyH * 0.13))
        : Math.max(22, Math.round(bodyH * 0.15));
      return (
        '<div style="' +
          'width:' + cw + 'px;' +
          'height:' + bodyH + 'px;' +
          'min-height:' + bodyH + 'px;' +
          'max-height:' + bodyH + 'px;' +
          'display:flex;flex-direction:column;' +
          'align-items:center;justify-content:center;' +
          'padding:' + CARD_PAD + 'px;' +
          'box-sizing:border-box;overflow:hidden;' +
          'text-align:center;' +
        '">' +
          '<div style="' +
            'font-size:' + fsText + 'px;' +
            'font-weight:900;' +
            'color:#1a2e4a;' +
            'font-family:Georgia,serif;' +
            'line-height:1.25;' +
            'word-break:break-word;' +
            'max-width:90%;' +
          '">' + (label || '') + '</div>' +
        '</div>'
      );
    }

    // ── Image-only / Image+Text ───────────────────────────────────────────
    return (
      '<div style="' +
        'width:' + cw + 'px;' +
        'height:' + bodyH + 'px;' +
        'min-height:' + bodyH + 'px;' +
        'max-height:' + bodyH + 'px;' +
        'display:flex;flex-direction:column;' +
        'padding:' + CARD_PAD + 'px ' + CARD_PAD + 'px 0;' +
        'box-sizing:border-box;overflow:hidden;' +
      '">' +
        (showTxt
          ? '<div style="font-size:' + fsLabel + 'px;font-weight:800;color:#1a2e4a;font-family:Segoe UI,Arial,sans-serif;flex-shrink:0;height:' + labelH + 'px;line-height:' + labelH + 'px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">' + label + '</div>'
          : '') +
        (showImg
          ? '<div style="flex:1;display:flex;align-items:center;justify-content:center;overflow:hidden;max-height:' + imgMaxH + 'px;">' +
              '<img src="' + card.imageUrl + '" style="max-width:' + imgMaxW + 'px;max-height:' + imgMaxH + 'px;object-fit:contain;display:block;" alt="' + label + '"/>' +
            '</div>'
          : '') +
      '</div>'
    );
  };

  // ── Build pages ───────────────────────────────────────────────────────────
  const totalSlots = cards.length + 1; // slot 0 = cover
  const totalPages = Math.ceil(totalSlots / cpp);

  const pagesHtml = Array.from({ length: totalPages }, (_, pi) => {

    // Build each row of cells
    let rowsHtml = '';
    for (let r = 0; r < rows; r++) {
      const ch = cellH(r); // exact height for this row
      let rowHtml = '';

      for (let c = 0; c < cols; c++) {
        const cw      = cellW(c); // exact width for this column
        const slotIdx = pi * cpp + r * cols + c;
        const isCover = slotIdx === 0;
        const cardIdx = slotIdx - 1;
        const isEmpty = !isCover && cardIdx >= cards.length;

        const inner = isEmpty
          ? '<div style="width:' + cw + 'px;height:' + (ch - FOOTER_H) + 'px;"></div>' +
            '<div style="width:' + cw + 'px;height:' + FOOTER_H + 'px;border-top:1px solid #D1D9E6;"></div>'
          : (isCover ? coverBody(cw, ch) : cardBody(cards[cardIdx], cw, ch)) +
            footer(isCover, cw);

        rowHtml +=
          '<div style="' +
            'width:' + cw + 'px;' +
            'height:' + ch + 'px;' +
            'min-height:' + ch + 'px;' +
            'max-height:' + ch + 'px;' +
            'display:flex;flex-direction:column;' +
            'box-sizing:border-box;overflow:hidden;' +
            'background:#fff;' +
            'outline:1px dashed #BBBBBB;' +
          '">' + inner + '</div>';
      }

      // Each row is a flex row of exact width PAGE_W and exact height ch
      rowsHtml +=
        '<div style="' +
          'width:' + PAGE_W + 'px;' +
          'height:' + ch + 'px;' +
          'min-height:' + ch + 'px;' +
          'max-height:' + ch + 'px;' +
          'display:flex;flex-direction:row;' +
          'overflow:hidden;flex-shrink:0;' +
        '">' + rowHtml + '</div>';
    }

    // Page: flex-column of rows, exactly PAGE_W × PAGE_H
    return (
      '<div style="' +
        'width:' + PAGE_W + 'px;' +
        'height:' + PAGE_H + 'px;' +
        'min-height:' + PAGE_H + 'px;' +
        'max-height:' + PAGE_H + 'px;' +
        'display:flex;flex-direction:column;' +
        'page-break-after:always;' +
        'overflow:hidden;box-sizing:border-box;' +
        'background:#fff;flex-shrink:0;' +
      '">' + rowsHtml + '</div>'
    );
  }).join('');

  // ── Print window ──────────────────────────────────────────────────────────
  const w = window.open('', '_blank');
  w.document.write(
    '<!DOCTYPE html><html><head>' +
    '<title>' + (title || 'Flashcards') + '</title>' +
    '<style>' +
  '* { margin:0; padding:0; box-sizing:border-box; }' +
  'body { background:#888; }' +
  '@media screen { .screen-body { padding:20px; gap:16px; } }' +
  '@media print {' +
    '@page { size:A4 portrait; margin:0; }' +
    'body { background:white; margin:0 !important; padding:0 !important; }' +
    '* { -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; }' +
  '}' +
'</style>' +
    '</head>' +
    '<body class="screen-body" style="display:flex;flex-direction:column;align-items:center;gap:16px;">' +
    pagesHtml +
    '<scr' + 'ipt>setTimeout(function(){ window.print(); }, 1000);</scr' + 'ipt>' +
    '</body></html>'
  );
  w.document.close();
}
// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
export default function FlashcardGenerator() {
  const { user, getAuthHeader } = useAuth();
  const role     = user?.role || "teacher";
  const readOnly = role === "parent";

  // ── Sets (left sidebar) ─────────────────────────────────────────────────
  const [sets,        setSets]        = useState([]);
  const [setsLoading, setSetsLoading] = useState(true);

  // ── Active set editor state ─────────────────────────────────────────────
  const [activeSetId,       setActiveSetId]       = useState(null);
  const [setTitle,          setSetTitle]           = useState("My Flashcard Set");
  const [cards,             setCards]              = useState([]);
  const [cardType,          setCardType]           = useState("image-text");
  const [layout,            setLayout]             = useState(LAYOUTS[1]);
  const [language,          setLanguage]           = useState("en");
  const [assignedChildren,  setAssignedChildren]   = useState([]);
  const [setIndicators,     setSetIndicators]      = useState([]);
  const [isSaving,          setIsSaving]           = useState(false);
  const [isDirty,           setIsDirty]            = useState(false);

  // ── Children list ────────────────────────────────────────────────────────
  const [children, setChildren] = useState([]);

  // ── Shared NCF development taxonomy (40 indicators / 5 domains) ─────────
  // Same source as CollectionManager — used to tag this set (and its cards)
  // so SM-2/BKT engagement can later roll up to NCF indicator progress.
  const [taxonomy, setTaxonomy] = useState(null);
  useEffect(() => {
    apiFetch("GET", "/collection/taxonomy", null, getAuthHeader())
      .then(d => { if (d.success) setTaxonomy(d.taxonomy); })
      .catch(() => {});
  }, []);

// ── UI state ────────────────────────────────────────────────────────────
const [showPicker,   setShowPicker]   = useState(false);
const [showAssign,   setShowAssign]   = useState(false);
const [showPreview,  setShowPreview]  = useState(false);
const [toast,        setToast]        = useState({ msg:"", type:"success" });
const [dragOver,     setDragOver]     = useState(false);
const [confirmDelete, setConfirmDelete] = useState(null); 
// ── PDF / print settings (reads showLogos from /settings) ───────────────
const [settings, setSettings] = useState({ showLogos: true });

useEffect(() => {
  apiFetch("GET", "/settings", null, getAuthHeader())
    .then(d => { if (d.success) setSettings(d.settings); })
    .catch(() => {}); // silently fall back to default { showLogos: true }
}, []);

  const fileInputRef = useRef();
  const toast$ = (msg, type="success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg:"", type:"success" }), 3000);
  };

  // ── Load sets on mount ───────────────────────────────────────────────────
  const loadSets = useCallback(async () => {
    setSetsLoading(true);
    try {
      const d = await apiFetch("GET", "/flashcard?limit=200", null, getAuthHeader());
      const list = d.flashcards || [];
      setSets(list);
      if (list.length > 0 && !activeSetId) {
        loadSet(list[0]);
      }
    } catch {}
    setSetsLoading(false);
  }, [getAuthHeader]);

  useEffect(() => { loadSets(); }, []);

  // ── Load children ────────────────────────────────────────────────────────
  useEffect(() => {
    if (readOnly) return;
    apiFetch("GET", "/children?limit=200", null, getAuthHeader())
      .then(d => setChildren(Array.isArray(d?.children) ? d.children : []))
      .catch(() => {});
  }, []);

  // ── Load a set into the editor ───────────────────────────────────────────
  const loadSet = (s) => {
    setActiveSetId(s._id);
    setSetTitle(s.title || "");
    setCardType(s.cardType || "image-text");
    setLayout(LAYOUTS.find(l => l.id === s.layoutId) || LAYOUTS[1]);
    setLanguage(s.language || "en");
    setAssignedChildren(s.assignedChildren || []);
    setSetIndicators(s.indicators || []);
    const restored = (s.cards || []).map((c, i) => ({
      id:       `set-${c.cardId || i}`,
      imageUrl: c.imageUrl || makeSvg("🃏", "#E8F0FE"),
      labels:   c.labels   || { en: c.cardLabel || "" },
      category: c.category || "Custom",
      indicators: c.indicators || [],
      selected: true,
    }));
    setCards(restored);
    setIsDirty(false);
  };

  // ── New blank set ────────────────────────────────────────────────────────
  const newSet = () => {
    setActiveSetId(null);
    setSetTitle("New Flashcard Set");
    setCards([]);
    setCardType("image-text");
    setLayout(LAYOUTS[1]);
    setLanguage("en");
    setAssignedChildren([]);
    setSetIndicators([]);
    setIsDirty(false);
    setShowPreview(false);
  };

  // ── Save (POST or PUT) ───────────────────────────────────────────────────
  const saveSet = async () => {
    const selectedCards = cards.filter(c => c.selected);
    if (selectedCards.length === 0) {
      toast$("Select at least one image first", "error");
      return;
    }
    setIsSaving(true);
    try {
      const cardPayload = selectedCards.map(c => ({
        cardId:    c.id,
        cardLabel: c.labels?.en || "",
        imageUrl:  (c.imageUrl?.startsWith("data:image/svg") || c.imageUrl?.startsWith("http"))
                     ? c.imageUrl : "",
        labels:    c.labels || {},
        category:  c.category || "Custom",
        indicators: c.indicators || [],
      }));
      const body = {
        title:            setTitle,
        cardType,
        language,
        layoutId:         layout.id,
        cards:            cardPayload,
        assignedChildren,
        indicators:       setIndicators,
      };
      const method = activeSetId ? "PUT"  : "POST";
      const path   = activeSetId ? `/flashcard/${activeSetId}` : "/flashcard";
      const d = await apiFetch(method, path, body, getAuthHeader());
      const saved = d.flashcard;
      setActiveSetId(saved._id);
      setIsDirty(false);
      setSets(prev => {
        const exists = prev.find(s => s._id === saved._id);
        return exists
          ? prev.map(s => s._id === saved._id ? { ...s, ...saved } : s)
          : [saved, ...prev];
      });
      toast$(`✅ "${setTitle}" saved (${cardPayload.length} cards)`);
    } catch (e) {
      toast$(`❌ ${e.message}`, "error");
    }
    setIsSaving(false);
  };

  // ── Delete set ───────────────────────────────────────────────────────────
  const deleteSet = async (id) => {
    setConfirmDelete(id);
  };
  
  const confirmDeleteSet = async () => {
    const id = confirmDelete;
    setConfirmDelete(null);
    try {
      await apiFetch("DELETE", `/flashcard/${id}`, null, getAuthHeader());
      setSets(prev => prev.filter(s => s._id !== id));
      if (activeSetId === id) newSet();
      toast$("🗑 Set deleted");
    } catch (e) {
      toast$(`❌ ${e.message}`, "error");
    }
  };

  // ── Pick from collection ─────────────────────────────────────────────────
  const handlePickFromCollection = (picked) => {
    const existingUrls = new Set(cards.map(c => c.imageUrl));
    const newCards = picked
      .filter(item => !existingUrls.has(item.imageUrl))
      .map(item => ({
        id:       `col-${item.id}`,
        imageUrl: item.imageUrl,
        labels:   item.labels,
        category: item.category || "Custom",
        indicators: item.indicators || [], // inherited from Collection — editable below
        selected: true,
      }));
    setCards(p => [...p, ...newCards]);
    setShowPicker(false);
    setIsDirty(true);
    toast$(`✅ Added ${newCards.length} image${newCards.length !== 1 ? "s" : ""}`);
  };

  // ── File upload ──────────────────────────────────────────────────────────
  const handleFileUpload = useCallback(files => {
    Array.from(files).forEach(file => {
      if (!file.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = e => {
        const name = file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ");
        setCards(p => [...p, {
          id:       `upload-${Date.now()}-${Math.random()}`,
          imageUrl: e.target.result,
          labels:   { en:name, ml:name, hi:name, ta:name, te:name },
          category: "Custom",
          indicators: [],
          selected: true,
        }]);
        setIsDirty(true);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  // ── Card ops ─────────────────────────────────────────────────────────────
  const toggleCard  = id => { setCards(p => p.map(c => c.id === id ? { ...c, selected: !c.selected } : c)); setIsDirty(true); };
  const removeCard  = id => { setCards(p => p.filter(c => c.id !== id)); setIsDirty(true); };
  const selectAll   = ()  => setCards(p => p.map(c => ({ ...c, selected: true  })));
  const deselectAll = ()  => setCards(p => p.map(c => ({ ...c, selected: false })));

  const updateCardLabel = (id, lang, value) => {
    setCards(p => p.map(c => {
      if (c.id !== id) return c;
      const updatedLabels = { ...c.labels, [lang]: value };
      return { ...c, labels: updatedLabels };
    }));
    setIsDirty(true);
  };

  // ── Set-level indicator tagging ──────────────────────────────────────────
  const toggleSetIndicator = (id) => {
    setSetIndicators(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
    setIsDirty(true);
  };

  // ── Assign children save ─────────────────────────────────────────────────
  const handleAssignSave = async (childIds) => {
    setAssignedChildren(childIds);
    setShowAssign(false);
    setIsDirty(true);
    if (activeSetId) {
      try {
        await apiFetch("PUT", `/flashcard/${activeSetId}`, {
          title: setTitle, cardType, language, layoutId: layout.id,
          cards: cards.filter(c => c.selected).map(c => ({
            cardId: c.id, cardLabel: c.labels?.en || "",
            imageUrl: c.imageUrl?.startsWith("http") ? c.imageUrl : "",
            labels: c.labels || {}, category: c.category || "Custom",
            indicators: c.indicators || [],
          })),
          assignedChildren: childIds,
          indicators: setIndicators,
        }, getAuthHeader());
        setSets(prev => prev.map(s => s._id === activeSetId ? { ...s, assignedChildren: childIds } : s));
        toast$(`✅ Assigned to ${childIds.length} child${childIds.length !== 1 ? "ren" : ""}`);
      } catch (e) { toast$(`❌ ${e.message}`, "error"); }
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────
  const selectedCards = cards.filter(c => c.selected);
  const assignedNames = assignedChildren
    .map(id => children.find(c => c._id === id)?.name)
    .filter(Boolean);

  // Domains touched by this set right now (set-level tags ∪ every card's tags)
  // — purely a live preview; the server derives & stores the authoritative
  // `domains` field on save.
  const touchedDomains = (() => {
    if (!taxonomy) return [];
    const all = new Set(setIndicators);
    selectedCards.forEach(c => (c.indicators || []).forEach(id => all.add(id)));
    const domainIds = new Set();
    taxonomy.indicators.forEach(ind => { if (all.has(ind.id)) domainIds.add(ind.domainId); });
    return taxonomy.domains.filter(d => domainIds.has(d.id));
  })();

  // ════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ display:"flex", height:"calc(100vh - 60px)", overflow:"hidden",
                  fontFamily:"'Segoe UI',Arial,sans-serif", background:"#f0f4ff" }}>
      <Toast msg={toast.msg} type={toast.type} />
      {confirmDelete && (
  <div style={{
    position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)",
    background:"#1a2e4a", color:"white", borderRadius:12,
    padding:"14px 20px", zIndex:700,
    boxShadow:"0 8px 32px rgba(0,0,0,.35)",
    display:"flex", alignItems:"center", gap:14,
    fontSize:13, fontWeight:600, animation:"fg-slideUp .2s ease",
    whiteSpace:"nowrap",
  }}>
    <span>🗑 Delete this set? This cannot be undone.</span>
    <button onClick={confirmDeleteSet}
      style={{ background:"#C0392B", color:"white", border:"none",
               borderRadius:7, padding:"6px 14px", fontWeight:700,
               fontSize:12, cursor:"pointer" }}>
      Delete
    </button>
    <button onClick={() => setConfirmDelete(null)}
      style={{ background:"rgba(255,255,255,.15)", color:"white", border:"none",
               borderRadius:7, padding:"6px 14px", fontWeight:700,
               fontSize:12, cursor:"pointer" }}>
      Cancel
    </button>
  </div>
)}
      {/* ── LEFT: Sets Sidebar ── */}
      <SetsSidebar
        sets={sets}
        activeId={activeSetId}
        onSelect={loadSet}
        onNew={newSet}
        onDelete={deleteSet}
        loading={setsLoading}
      />

      {/* ── RIGHT: Editor ── */}
      <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column" }}>

        {/* Editor top bar */}
        <div style={{ background:"white", borderBottom:"1px solid #dce3f5", padding:"0 20px",
                      display:"flex", alignItems:"center", gap:12, flexShrink:0, minHeight:54 }}>

          <input
            value={setTitle}
            onChange={e => { setSetTitle(e.target.value); setIsDirty(true); }}
            style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:18, fontWeight:800,
                     color:"#003366", border:"none", outline:"none", background:"transparent",
                     flex:1, minWidth:0 }}
            placeholder="Set title…"
          />

          {isDirty && (
            <span style={{ fontSize:10, color:"#E65100", fontWeight:700, background:"#FFF3E0",
                           padding:"2px 8px", borderRadius:8, flexShrink:0 }}>
              Unsaved changes
            </span>
          )}

          <select value={language} onChange={e => { setLanguage(e.target.value); setIsDirty(true); }}
            style={{ border:"1.5px solid #dce3f5", borderRadius:8, padding:"5px 8px",
                     fontSize:12, outline:"none", color:"#003366", background:"white" }}>
            {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.flag} {l.name}</option>)}
          </select>

          {!readOnly && (
            <button onClick={saveSet} disabled={isSaving}
              style={{ padding:"7px 18px", background: isSaving ? "#ccc" : "#003366",
                       color:"white", border:"none", borderRadius:8, fontWeight:700,
                       fontSize:12, cursor: isSaving ? "not-allowed" : "pointer",
                       display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
              {isSaving ? <><Spinner size={14} /> Saving…</> : activeSetId ? "💾 Update Set" : "💾 Save Set"}
            </button>
          )}

          <button onClick={() => selectedCards.length > 0 && setShowPreview(true)}
            disabled={selectedCards.length === 0}
            style={{ padding:"7px 14px", background: selectedCards.length > 0 ? "#1B6B45" : "#ccc",
                     color:"white", border:"none", borderRadius:8, fontWeight:700,
                     fontSize:12, cursor: selectedCards.length > 0 ? "pointer" : "not-allowed",
                     flexShrink:0 }}>
            🖨️ Export PDF {selectedCards.length > 0 ? `(${selectedCards.length})` : ""}
          </button>
        </div>

        {/* Editor body */}
        <div style={{ flex:1, padding:"20px", display:"grid",
                      gridTemplateColumns:"1fr 270px", gap:18, alignItems:"start" }}>

          {/* ── Main: image grid ── */}
          <div>
            {!readOnly && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                <button onClick={() => setShowPicker(true)}
                  style={{ padding:"12px 16px", background:"white", border:"2px solid #003366",
                           borderRadius:10, fontWeight:700, fontSize:13, color:"#003366",
                           cursor:"pointer", display:"flex", alignItems:"center",
                           justifyContent:"center", gap:7 }}>
                  🗂️ Browse Collection
                </button>
                <div
                  onDrop={e => { e.preventDefault(); setDragOver(false); handleFileUpload(e.dataTransfer.files); }}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onClick={() => fileInputRef.current?.click()}
                  style={{ border:`2px dashed ${dragOver ? "#003366" : "#a0b8d8"}`, borderRadius:10,
                           padding:"12px 16px", cursor:"pointer",
                           background: dragOver ? "#e8f0fe" : "white",
                           display:"flex", alignItems:"center", justifyContent:"center",
                           gap:7, fontWeight:700, color:"#666", fontSize:13,
                           transition:"all .15s" }}>
                  📁 Upload Image
                  <span style={{ fontSize:10, color:"#aaa", fontWeight:400 }}>PNG · JPG</span>
                  <input ref={fileInputRef} type="file" multiple accept="image/*"
                    style={{ display:"none" }}
                    onChange={e => handleFileUpload(e.target.files)} />
                </div>
              </div>
            )}

            {cards.length > 0 && (
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                <span style={{ fontSize:12, color:"#666", flex:1 }}>
                  <strong style={{ color:"#003366" }}>{selectedCards.length}</strong>
                  <span style={{ color:"#888" }}> of {cards.length} selected for this set</span>
                </span>
                <button onClick={selectAll}
                  style={{ padding:"4px 10px", fontSize:11, background:"#e8f0fe", color:"#003366",
                           border:"none", borderRadius:6, cursor:"pointer", fontWeight:600 }}>
                  Select All
                </button>
                <button onClick={deselectAll}
                  style={{ padding:"4px 10px", fontSize:11, background:"white", color:"#666",
                           border:"1px solid #dce3f5", borderRadius:6, cursor:"pointer", fontWeight:600 }}>
                  Deselect All
                </button>
              </div>
            )}

            {cards.length === 0 ? (
              <div style={{ background:"white", borderRadius:14, padding:"60px 20px",
                            border:"2px dashed #c5d5ee", textAlign:"center", color:"#b0becc" }}>
                <div style={{ fontSize:48, marginBottom:12 }}>🃏</div>
                <div style={{ fontSize:16, fontWeight:800, color:"#8fa5bc", marginBottom:6 }}>
                  {activeSetId ? "This set has no images" : "Start building your set"}
                </div>
                <div style={{ fontSize:12, lineHeight:1.7, marginBottom:20 }}>
                  Browse your collection to pick images,<br />or upload new ones directly.
                </div>
                {!readOnly && (
                  <button onClick={() => setShowPicker(true)}
                    style={{ padding:"10px 24px", background:"#003366", color:"white",
                             border:"none", borderRadius:9, fontWeight:700, fontSize:13, cursor:"pointer" }}>
                    🗂️ Browse Collection
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))", gap:10 }}>
                {cards.map(card => (
                  <CardTile
                    key={card.id}
                    card={card}
                    language={language}
                    selected={card.selected}
                    onToggle={toggleCard}
                    onRemove={removeCard}
                    onLabelChange={updateCardLabel}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Right: settings panel ── */}
          <div style={{ display:"flex", flexDirection:"column", gap:12, position:"sticky", top:20 }}>

            <div style={{ background:"white", borderRadius:12, padding:16,
                          border:"1.5px solid #e0e7f0", borderTop:`3px solid #003366` }}>
              <div style={{ fontSize:10, fontWeight:800, color:"#003366", marginBottom:10,
                            textTransform:"uppercase", letterSpacing:.8 }}>📋 Set Summary</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                {[
                  { label:"Total images", val: cards.length, color:"#003366" },
                  { label:"In this set",  val: selectedCards.length, color:"#1B6B45" },
                ].map(s => (
                  <div key={s.label} style={{ textAlign:"center", background:"#f5f8ff",
                                              borderRadius:9, padding:"10px 6px" }}>
                    <div style={{ fontSize:24, fontWeight:900, color:s.color, lineHeight:1 }}>{s.val}</div>
                    <div style={{ fontSize:9, color:"#8fa5bc", fontWeight:600, marginTop:3,
                                  textTransform:"uppercase" }}>{s.label}</div>
                  </div>
                ))}
              </div>
              {touchedDomains.length > 0 && (
                <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid #f0f4ff" }}>
                  <div style={{ fontSize:9, fontWeight:700, color:"#8fa5bc", textTransform:"uppercase",
                                letterSpacing:.6, marginBottom:6 }}>Domains touched</div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                    {touchedDomains.map(d => (
                      <span key={d.id} title={d.label}
                        style={{ fontSize:11, fontWeight:700, color:"#003366", background:"#EBF4FB",
                                 padding:"2px 8px", borderRadius:8 }}>
                        {d.icon} {d.short}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ background:"white", borderRadius:12, padding:16, border:"1.5px solid #e0e7f0" }}>
              <div style={{ fontSize:10, fontWeight:800, color:"#003366", marginBottom:10,
                            textTransform:"uppercase", letterSpacing:.8 }}>🃏 Card Type</div>
              {CARD_TYPES.map(ct => (
                <button key={ct.id} onClick={() => { if (!readOnly) { setCardType(ct.id); setIsDirty(true); }}}
                  style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 10px", marginBottom:5,
                           width:"100%", border: cardType===ct.id ? "2px solid #003366" : "1.5px solid #dce3f5",
                           borderRadius:8, background: cardType===ct.id ? "#e8f0fe" : "white",
                           cursor: readOnly ? "default" : "pointer", fontSize:11, fontWeight:600,
                           color: cardType===ct.id ? "#003366" : "#555", textAlign:"left" }}>
                  <span style={{ fontSize:15 }}>{ct.icon}</span>
                  <span>{ct.label}</span>
                  {cardType===ct.id && <span style={{ marginLeft:"auto", color:"#003366", fontSize:11 }}>✓</span>}
                </button>
              ))}
            </div>

            <div style={{ background:"white", borderRadius:12, padding:16, border:"1.5px solid #e0e7f0" }}>
              <div style={{ fontSize:10, fontWeight:800, color:"#003366", marginBottom:10,
                            textTransform:"uppercase", letterSpacing:.8 }}>📐 PDF Layout</div>
              <div style={{ display:"flex", gap:6 }}>
                {LAYOUTS.map(l => (
                  <button key={l.id} onClick={() => { if (!readOnly) { setLayout(l); setIsDirty(true); }}}
                    style={{ flex:1, padding:"8px 4px", textAlign:"center",
                             border: layout.id===l.id ? "2px solid #003366" : "1.5px solid #dce3f5",
                             borderRadius:8, background: layout.id===l.id ? "#e8f0fe" : "white",
                             cursor: readOnly ? "default" : "pointer", fontSize:11,
                             fontWeight:700, color: layout.id===l.id ? "#003366" : "#666" }}>
                    {l.label}
                    <br /><span style={{ fontSize:9, fontWeight:400, color:"#888" }}>{l.id}</span>
                  </button>
                ))}
              </div>
            </div>

            {!readOnly && (
              <div style={{ background:"white", borderRadius:12, padding:16, border:"1.5px solid #e0e7f0" }}>
                <div style={{ fontSize:10, fontWeight:800, color:"#003366", marginBottom:10,
                              textTransform:"uppercase", letterSpacing:.8 }}>
                  🎯 Development Indicators (NCF)
                </div>
                <div style={{ fontSize:10, color:"#aaa", marginBottom:10, lineHeight:1.6 }}>
                  What does this set, as a whole, help practice? Individual cards can carry
                  their own tags too (inherited from Collection when picked).
                </div>
                <IndicatorTagPicker taxonomy={taxonomy} selected={setIndicators} onToggle={toggleSetIndicator} compact/>
                {setIndicators.length > 0 && (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginTop:8 }}>
                    {setIndicators.map(id => {
                      const ind = taxonomy?.indicators.find(i => i.id === id);
                      return (
                        <span key={id} style={{ background:"#EBF4FB", color:"#003366", fontSize:10,
                                                fontWeight:700, padding:"2px 8px", borderRadius:10 }}>
                          {id}{ind ? ` · ${ind.label}` : ""}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {!readOnly && (
              <div style={{ background:"white", borderRadius:12, padding:16, border:"1.5px solid #e0e7f0" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                  <div style={{ fontSize:10, fontWeight:800, color:"#003366",
                                textTransform:"uppercase", letterSpacing:.8 }}>👤 Assign to Children</div>
                  <button onClick={() => setShowAssign(true)}
                    style={{ fontSize:10, fontWeight:700, color:"#003366", background:"#e8f0fe",
                             border:"none", borderRadius:6, padding:"3px 9px", cursor:"pointer" }}>
                    {assignedChildren.length > 0 ? "Edit" : "+ Assign"}
                  </button>
                </div>
                {assignedChildren.length === 0 ? (
                  <div style={{ fontSize:11, color:"#b0becc", fontStyle:"italic", lineHeight:1.6 }}>
                    Not assigned to any children.<br />
                    Assigned sets will appear on the Parent Dashboard.
                  </div>
                ) : (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                    {assignedNames.map(name => (
                      <span key={name} style={{ fontSize:10, fontWeight:700, background:"#EBF4FB",
                                               color:"#003366", padding:"3px 9px", borderRadius:10 }}>
                        {name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => selectedCards.length > 0 && setShowPreview(true)}
              disabled={selectedCards.length === 0}
              style={{ padding:"12px", background: selectedCards.length > 0 ? "#003366" : "#e0e7f0",
                       color: selectedCards.length > 0 ? "white" : "#aaa",
                       border:"none", borderRadius:10, fontWeight:700, fontSize:12,
                       cursor: selectedCards.length > 0 ? "pointer" : "not-allowed",
                       boxShadow: selectedCards.length > 0 ? "0 4px 16px rgba(0,51,102,.25)" : "none" }}>
              {selectedCards.length > 0
                ? `🖨️ Export PDF (${selectedCards.length} cards)`
                : "Select images to export"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {showPicker && (
        <CollectionPickerModal
          onPick={handlePickFromCollection}
          onClose={() => setShowPicker(false)}
          getAuthHeader={getAuthHeader}
        />
      )}

      {showAssign && (
        <AssignModal
          children={children}
          current={assignedChildren}
          onSave={handleAssignSave}
          onClose={() => setShowAssign(false)}
        />
      )}

      {/* ── PDF Preview Modal ── */}
      {showPreview && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.55)", zIndex:500,
                      display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
          onClick={() => setShowPreview(false)}>
          <div style={{ background:"white", borderRadius:16, width:520, maxHeight:"80vh",
                        display:"flex", flexDirection:"column",
                        boxShadow:"0 24px 60px rgba(0,0,0,.28)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:"16px 22px", borderBottom:"1px solid #e0e7f0", display:"flex",
                          alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ fontWeight:800, fontSize:15, color:"#003366" }}>🖨️ Export PDF</div>
              <button onClick={() => setShowPreview(false)}
                style={{ background:"#f0f4ff", border:"none", borderRadius:8,
                         padding:"6px 12px", cursor:"pointer", color:"#666" }}>✕</button>
            </div>
            <div style={{ padding:"20px 24px", overflowY:"auto" }}>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20 }}>
                {[
                  { icon:"🃏", val:selectedCards.length, label:"Cards",  color:"#003366" },
                  { icon:"📄", val:Math.ceil(selectedCards.length/layout.id)||0, label:"Pages", color:"#444" },
                  { icon:"📐", val:layout.label, label:"Layout", color:"#7B2D8B" },
                ].map(s => (
                  <div key={s.label} style={{ background:"#f5f8ff", borderRadius:10, padding:"14px",
                                              textAlign:"center", border:"1px solid #e0e7f0" }}>
                    <div style={{ fontSize:20 }}>{s.icon}</div>
                    <div style={{ fontSize:22, fontWeight:900, color:s.color, lineHeight:1 }}>{s.val}</div>
                    <div style={{ fontSize:10, color:"#888", marginTop:3 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ background:"#f5f8ff", borderRadius:10, padding:"12px 16px",
                            border:"1px solid #e0e7f0", marginBottom:20, fontSize:12 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                  {[
                    ["Title",     setTitle],
                    ["Card Type", CARD_TYPES.find(c=>c.id===cardType)?.label],
                    ["Language",  LANGUAGES.find(l=>l.code===language)?.name],
                    ["Layout",    `${layout.label} (${layout.id}/page)`],
                  ].map(([k,v]) => (
                    <div key={k} style={{ display:"flex", gap:8, padding:"4px 0",
                                          borderBottom:"1px solid #e8edf8" }}>
                      <span style={{ color:"#aaa", fontSize:11, minWidth:72 }}>{k}:</span>
                      <span style={{ fontWeight:600, color:"#1a2e4a", fontSize:11 }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <button
                onClick={() => { generatePDF(selectedCards, cardType, layout, language, setTitle, settings).then(() => setShowPreview(false)); }}
                style={{ width:"100%", padding:"13px", background:"#003366", color:"white",
                         border:"none", borderRadius:10, fontWeight:700, fontSize:14, cursor:"pointer",
                         boxShadow:"0 4px 16px rgba(0,51,102,.3)" }}>
                🖨️ Open & Print PDF
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fg-spin    { to { transform: rotate(360deg) } }
        @keyframes fg-slideUp { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }
        .card-remove-btn { opacity: 0 !important; }
        *:hover > .card-remove-btn { opacity: 1 !important; }
        .card-edit-btn   { opacity: 0 !important; }
        *:hover > * > .card-edit-btn { opacity: 1 !important; }
      `}</style>
    </div>
  );
}